"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ensureProjectDocumentsLoaded = ensureProjectDocumentsLoaded;
exports.presentProjects = presentProjects;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _rdfList = require("../shared/rdfList");
/* The following functions communityNodesForSubject and ensureProjectDocumentsLoaded 
   and a few updates in presentProjects are generated by AI Model: GPT-5.3-Codex */
/* Prompt: Image and business type are not displaying, could it be that I didn't load
  the document? */
function communityNodesForSubject(subject, store, doc) {
  const inProfileDoc = store.each(subject, _solidUi.ns.solid('community'), null, doc);
  const inAnyDoc = store.each(subject, _solidUi.ns.solid('community'), null, null);
  const rawNodes = [...inProfileDoc, ...inAnyDoc];
  const expandedNodes = rawNodes.flatMap(node => {
    const hasCollectionElements = Array.isArray(node?.elements);
    const isListByTriples = (node.termType === 'BlankNode' || node.termType === 'NamedNode') && Boolean(store.any(node, _solidUi.ns.rdf('first')));
    return hasCollectionElements || isListByTriples ? (0, _rdfList.expandRdfList)(store, node) : [node];
  });
  const seen = new Set();
  const nodes = [];
  for (const node of expandedNodes) {
    const key = `${node?.termType || ''}:${node?.value || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    nodes.push(node);
  }
  return nodes;
}
function clearStaleRequestedState(fetcher, docUrl) {
  const requested = fetcher?.requested;
  if (!requested || typeof requested !== 'object') return undefined;
  const previous = requested[docUrl];
  const shouldClear = typeof previous === 'string' && previous !== 'requested' && previous !== 'done' || typeof previous === 'number' && previous >= 400;
  if (shouldClear) {
    delete requested[docUrl];
  }
  return previous;
}
function linkedProjectNodeFromCommunityNode(node, store) {
  if (!node) return null;
  if (node.termType === 'NamedNode') return node;
  if (node.termType === 'Literal') {
    try {
      return (0, _rdflib.sym)(node.value);
    } catch {
      return null;
    }
  }
  if (node.termType !== 'BlankNode') return null;
  const candidatePredicates = [_solidUi.ns.solid('publicId'), _solidUi.ns.schema('url'), _solidUi.ns.foaf('homepage'), _solidUi.ns.rdfs('seeAlso'), _solidUi.ns.schema('sameAs'), _solidUi.ns.schema('mainEntityOfPage')];
  for (const predicate of candidatePredicates) {
    const linked = store.any(node, predicate);
    if (!linked?.value) continue;
    if (linked.termType === 'NamedNode') {
      return linked;
    }
    if (linked.termType === 'Literal') {
      try {
        return (0, _rdflib.sym)(linked.value);
      } catch {
        // Keep trying the next predicate.
      }
    }
  }
  const outgoing = store.statementsMatching(node, null, null, null);
  const firstNamedNode = outgoing.find(statement => statement.object.termType === 'NamedNode');
  if (firstNamedNode?.object?.value) {
    return firstNamedNode.object;
  }
  return null;
}
async function ensureProjectDocumentsLoaded(subject, store) {
  const profileDoc = subject.doc();
  const fetcher = store?.fetcher;
  if (!fetcher || typeof fetcher.load !== 'function') {
    return;
  }
  const communityNodes = communityNodesForSubject(subject, store, profileDoc);
  const docsToLoad = new Map();
  for (const node of communityNodes) {
    const linkedProjectNode = linkedProjectNodeFromCommunityNode(node, store);
    if (!linkedProjectNode) continue;
    const projectDoc = linkedProjectNode.doc();
    docsToLoad.set(projectDoc.value, projectDoc);
  }
  if (!docsToLoad.size) {
    return;
  }
  await Promise.all(Array.from(docsToLoad.values()).map(async doc => {
    const previousRequested = clearStaleRequestedState(fetcher, doc.value);
    try {
      void previousRequested;
      await fetcher.load(doc);
    } catch {
      // Ignore individual project doc load failures so one bad doc doesn't block others.
    }
  }));
}
function projectNodeToUrl(node) {
  if (!node) return '';
  if (node.termType === 'NamedNode') return node.value || '';
  if (node.termType === 'Literal') return node.value || '';
  return '';
}
function normalizeUrlKey(value) {
  const text = (value || '').trim();
  if (!text) return '';
  try {
    return new URL(text).href;
  } catch {
    return text;
  }
}
function toProjectNamedNode(node, url) {
  if (node && node.termType === 'NamedNode') return node;
  try {
    return (0, _rdflib.sym)(url);
  } catch {
    return null;
  }
}
async function presentProjects(subject, store) {
  await ensureProjectDocumentsLoaded(subject, store);
  const doc = subject.doc();
  const seen = new Set();
  const communityNodes = communityNodesForSubject(subject, store, doc);
  const projects = communityNodes.map(communityNode => {
    const linkedProjectNode = linkedProjectNodeFromCommunityNode(communityNode, store);
    const url = linkedProjectNode?.value || projectNodeToUrl(communityNode);
    const key = normalizeUrlKey(url);
    const projectNode = linkedProjectNode || toProjectNamedNode(communityNode, url);
    const projectDoc = projectNode?.doc();
    const name = projectNode ? store.anyValue(projectNode, _solidUi.ns.foaf('name'), null, projectDoc || null) || undefined : undefined;
    const orgName = projectNode ? store.anyValue(projectNode, _solidUi.ns.vcard('organization-name'), null, projectDoc || null) || undefined : undefined;
    const title = name || (projectNode ? _solidUi.utils.label(projectNode) : _solidUi.utils.label(key));
    const imageUrl = projectNode ? _solidUi.widgets.findImage(projectNode) || (_solidUi.widgets.findImageFromURI?.(projectNode) ?? undefined) : undefined;
    let types = [];
    if (projectNode) {
      types = store.each(projectNode, _solidUi.ns.rdf('type'), null, projectDoc || null);
    }
    const category = types.find(t => t.termType === 'NamedNode' && (t.value === _solidUi.ns.schema('Project').value || t.value === _solidUi.ns.schema('Community').value));
    const categoryValue = category?.value === _solidUi.ns.schema('Project').value ? 'project' : category?.value === _solidUi.ns.schema('Community').value ? 'community' : 'unknown';
    if (!url || !key || seen.has(key)) {
      return null;
    }
    seen.add(key);
    return {
      url,
      title,
      name,
      orgName,
      imageUrl,
      category: categoryValue,
      entryNode: communityNode
    };
  }).filter(project => Boolean(project));
  return projects;
}